import { api } from "./client";
import type { LeadConversionTarget, LeadInviteToApplyResultDto } from "./leads";
import type {
  BrokerAssignmentField,
  BulkBrokerActionResultDto,
  PagedQuery,
  PagedResult,
} from "@/types/api";

export type BrokerStatus =
  | "Prospect" | "Qualified" | "Onboarding" | "Certified"  // active pipeline
  | "Inactive" | "Suspended" | "Disqualified" | "Declined"; // archive

export const PIPELINE_STATUSES: BrokerStatus[] =
  ["Prospect", "Qualified", "Onboarding", "Certified"];
export const ARCHIVE_STATUSES: BrokerStatus[] =
  ["Inactive", "Suspended", "Disqualified", "Declined"];
export const isPipelineStatus = (s: BrokerStatus) =>
  PIPELINE_STATUSES.includes(s);
export const isArchiveStatus = (s: BrokerStatus) =>
  ARCHIVE_STATUSES.includes(s);

export type ArchiveTargetStatus = "Inactive" | "Suspended" | "Declined";
export type BrokerListView = "Pipeline" | "Archive" | "All";
/**
 * ⚠️ `ListingAgent` is SYSTEM-SET and must NOT join the `SOURCES` picker in
 * BrokerEditPage — it is set only by converting a lead that came from a Buyer
 * CRM listing agent, and a human choosing it would claim a provenance the
 * record does not have. Mirrors `LeadSource.ListingAgent`, which is absent from
 * the staff source pickers for the same reason. Escalation PR-2.
 */
export type BrokerSource =
  | "IBBA"
  | "MaSource"
  | "LinkedIn"
  | "Referral"
  | "BizBuySell"
  | "Other"
  | "ListingAgent";
// Lead Conversion Router (WS-3) — the broker's immutable payee posture.
export type BrokerType = "Independent" | "Junior";
export type QualificationResult = "Proceed" | "Hold" | "Decline";
export type TrainingStatus = "NotStarted" | "InProgress" | "Completed";
export type TrainingModuleType = "Compliance" | "Product" | "Process";
export type BrokerDocCategory = "MBA" | "ExhibitA" | "ExhibitB" | "ExhibitC" | "ExhibitD" | "EoCert" | "License" | "TrainingCert" | "Correspondence" | "Other";
export type BrokerActivityType =
  | "Call" | "Email" | "Meeting" | "Note" | "StatusChange" | "DocumentUploaded"
  | "CallLogged" | "TaskCreated" | "TaskCompleted" | "QualificationUpdated";
export type BrokerAlertType = "EoInsurance" | "License";
export type BrokerAlertSeverity = "Info" | "Warning" | "Critical" | "Expired";

// Broker "Import from files" (WS-E) — the AI qualification-import job state
// rides the broker detail DTO so the page can poll a long-running scan and
// fill the profile fields live. Enums arrive as STRINGS (JsonStringEnumConverter).
export type BrokerAiImportStatus =
  | "None" | "Pending" | "Running" | "Completed" | "Failed";
export type BrokerQualificationImportTriggerStatus =
  | "Started" | "AlreadyRunning" | "NoDocuments";

/** One imported field's provenance — where the value came from and whether
 *  it was applied to the broker record. */
export interface BrokerQualificationProvenanceDto {
  field: string;
  value: string | null;
  confidence: number;
  sourceDocumentId: string | null;
  sourceDocumentName: string | null;
  sourceSnippet: string | null;
  applied: boolean;
  importedAt: string;
}

/** A filed document the import skipped, with the reason. */
export interface BrokerQualificationImportSkippedDocDto {
  documentId: string;
  fileName: string;
  reason: string;
}

/** Result of POST /brokers/{id}/import-from-files. */
export interface BrokerQualificationImportTriggerResult {
  status: BrokerQualificationImportTriggerStatus;
  documentCount: number;
  message: string | null;
}

export interface BrokerSummaryDto {
  id: string;
  companyName: string;
  dba?: string | null;
  name?: string | null;
  status: BrokerStatus;
  source: BrokerSource;
  state?: string | null;
  eoExpirationDate?: string | null;
  licenseExpirationDate?: string | null;
  assignedC1LiaisonUserId?: string | null;
  assignedAt?: string | null;
  assignedByUserId?: string | null;
  createdAt: string;
  hasEoAlert: boolean;
  hasLicenseAlert: boolean;
  alertDaysUntilExpiry: number;
  // Broker Status Refactor — archive context surfaced on list rows
  archivedDate?: string | null;
  archivedByUserId?: string | null;
  archiveReason?: string | null;
  disqualificationReason?: string | null;
  // Broker self-apply follow-ups — true while the broker has a self-application
  // still in Submitted; drives the kanban "New applicant" badge.
  isNewApplicant?: boolean;
  // Broker Channel Batch (WS6) — teams + graduation. teamId != null means the
  // broker has graduated to a team (status stays Certified); readyToGraduate
  // drives the Certified-column "Ready to graduate" affordance.
  teamId?: string | null;
  teamName?: string | null;
  certifiedAt?: string | null;
  graduatedAt?: string | null;
  readyToGraduate?: boolean;
  // Lead Conversion Router (WS-3) — payee-type badge (optional per the
  // LoanSummaryDto FE convention; defaults to Independent on the wire).
  brokerType?: BrokerType;
  // PR 2 (broker-search disambiguation) — contact email, shown in every broker
  // picker alongside company + contact name.
  email?: string | null;
}

export interface BrokerContactDto {
  id: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

// UAT FixBatch-04 FIND-13a — three-state invite affordance on the broker
// header. Mirrors the lead-side Send Application Link, gated server-side by
// Leads.Convert. `canInvite` is false when the broker has no email on file.
export interface BrokerInviteStatusDto {
  state: "NotInvited" | "InviteSent" | "Active";
  contactEmail: string | null;
  canInvite: boolean;
  // Broker↔collection link — the linked portal account's id, so the active
  // indicator can link to /admin/portal-accounts/{id}. Null when none exists.
  collectionAccountId: string | null;
}
export interface BrokerQualificationDto {
  id: string;
  qualificationDate: string;
  evaluatorUserId: string;
  greenFlagsJson?: string | null;
  redFlagsJson?: string | null;
  dealFlowEstimate?: number | null;
  transactionSizeRange?: string | null;
  isIbbaMember: boolean;
  isMaSourceMember: boolean;
  hasWebsite: boolean;
  hasCrm: boolean;
  primarilyResidential: boolean;
  overallAssessment?: string | null;
  recommendation: QualificationResult;
  /** Author of the qualification; used to gate edit/remove to the creator
   *  (or an admin). Null for legacy rows. */
  createdByUserId?: string | null;
  /** Selected admin-configured field IDs, so the edit modal can re-check the
   *  right boxes. Null/absent for legacy rows that only used the booleans. */
  qualificationCheckIds?: string[] | null;
}
export interface BrokerTrainingDto {
  id: string;
  moduleName: string;
  moduleType: TrainingModuleType;
  status: TrainingStatus;
  assignedAt?: string | null;
  completedAt?: string | null;
  score?: number | null;
  certificateDocumentId?: string | null;
}
export interface BrokerDocumentDto {
  id: string;
  title: string;
  description?: string | null;
  category: BrokerDocCategory;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  version: number;
  isCurrentVersion: boolean;
  uploadedByUserId: string;
  createdAt: string;
}
export interface BrokerActivityDto {
  id: string;
  activityType: BrokerActivityType;
  description: string;
  performedByUserId: string;
  timestamp: string;
}
export interface BrokerLicenseDetailDto {
  licenseTypeIssuingOrganization?: string | null;
  licenseNumber?: string | null;
  expirationDate?: string | null;
  state?: string | null;
}

export interface BrokerDetailDto {
  id: string;
  companyName: string;
  dba?: string | null;
  /** The broker's own contact details — edited on the add/edit form and read
   *  by the Company card. The contacts array (with its isPrimary "preferred"
   *  pointer) is managed separately and does not drive these. */
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  status: BrokerStatus;
  source: BrokerSource;
  sourceDetail?: string | null;
  eoInsuranceProvider?: string | null;
  eoPolicyNumber?: string | null;
  eoExpirationDate?: string | null;
  eoCoverageAmount?: number | null;
  licenseNumber?: string | null;
  licenseState?: string | null;
  licenseExpirationDate?: string | null;
  linkedInProfile?: string | null;
  yearsOfExperience?: number | null;
  numberOfTransactionsClosed?: number | null;
  primaryAreasOfExpertise?: string | null;
  industriesServed?: string | null;
  geographicMarketsServed?: string | null;
  professionalCertificationsLicensesDesignations?: string | null;
  phoenixCapitalInterest?: string | null;
  businessBrokerageLicenseTypes: string[];
  licensingDetails: BrokerLicenseDetailDto[];
  website?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  qualificationScoreGreen?: number | null;
  qualificationScoreRed?: number | null;
  qualificationNotes?: string | null;
  mbaVersion?: number | null;
  mbaExecutedAt?: string | null;
  trainingCompletedAt?: string | null;
  trainingCertificateDocumentId?: string | null;
  portalAccessGrantedAt?: string | null;
  portalLoginEmail?: string | null;
  assignedC1LiaisonUserId?: string | null;
  assignedC0ManagerUserId?: string | null;
  assignedAt?: string | null;
  assignedByUserId?: string | null;
  notes?: string | null;
  // v3.0 enhancements
  tags: string[];
  hasDisciplinaryAction: boolean;
  disciplinaryNotes?: string | null;
  annualRefresherDueDate?: string | null;
  // Broker Status Refactor — archive + reactivation audit trail
  archivedDate?: string | null;
  archivedByUserId?: string | null;
  archiveReason?: string | null;
  reactivatedDate?: string | null;
  reactivatedByUserId?: string | null;
  disqualificationReason?: string | null;
  createdAt: string;
  modifiedAt?: string | null;
  contacts: BrokerContactDto[];
  qualifications: BrokerQualificationDto[];
  training: BrokerTrainingDto[];
  documents: BrokerDocumentDto[];
  recentActivity: BrokerActivityDto[];
  // Broker self-apply follow-ups — true while the broker has a self-application
  // still in Submitted; drives the detail-header "New applicant" pill.
  isNewApplicant?: boolean;
  // Broker Channel Batch (WS6) — teams + graduation (same semantics as the
  // summary DTO's fields).
  teamId?: string | null;
  teamName?: string | null;
  certifiedAt?: string | null;
  graduatedAt?: string | null;
  readyToGraduate?: boolean;
  // Broker Channel Batch (WS7a) — qualifications-profile fields beyond the
  // application import set, plus the approval-time import stamp.
  industrySpecialties?: string[] | null;
  transactionSizeRange?: string | null;
  largestTransactionClosed?: number | null;
  totalCareerTransactionVolume?: number | null;
  averageDealsPerYear?: number | null;
  currentActiveListings?: number | null;
  isIbbaMember?: boolean | null;
  isMaSourceMember?: boolean | null;
  otherProfessionalMemberships?: string | null;
  professionalBiography?: string | null;
  dealSourcingNotes?: string | null;
  languagesSpoken?: string | null;
  applicationImportedAt?: string | null;
  // Broker "Import from files" (WS-E) — AI qualification-import job state +
  // results, surfaced on the broker detail GET so the Qualifications tab
  // polls a running scan and renders provenance/skipped docs when it ends.
  aiImportStatus?: BrokerAiImportStatus;
  aiImportStartedAt?: string | null;
  aiImportFinishedAt?: string | null;
  aiImportFailureReason?: string | null;
  filesImportedAt?: string | null;
  /** Best-effort count of importable documents in the broker's folders;
   *  null = unknown. Drives the pre-flight on the import button. */
  importableDocumentCount?: number | null;
  qualificationImportProvenance?: BrokerQualificationProvenanceDto[] | null;
  qualificationImportSkippedDocuments?: BrokerQualificationImportSkippedDocDto[] | null;
  // Lead Conversion Router (WS-3) — payee type + resolved active covering firm.
  // A junior renders "Junior · {affiliatedBrokerageName}" linking to the firm;
  // the firm fields are null when there is no single active affiliation.
  brokerType?: BrokerType;
  affiliatedBrokerageId?: string | null;
  affiliatedBrokerageName?: string | null;
}

export interface UpsertBrokerPayload {
  companyName: string;
  dba?: string | null;
  /** The broker's own contact details, edited on the add/edit form. Email and
   *  phone are format-validated server-side. */
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  source: BrokerSource;
  sourceDetail?: string | null;
  eoInsuranceProvider?: string | null;
  eoPolicyNumber?: string | null;
  eoExpirationDate?: string | null;
  eoCoverageAmount?: number | null;
  licenseNumber?: string | null;
  licenseState?: string | null;
  licenseExpirationDate?: string | null;
  linkedInProfile?: string | null;
  yearsOfExperience?: number | null;
  numberOfTransactionsClosed?: number | null;
  primaryAreasOfExpertise?: string | null;
  industriesServed?: string | null;
  geographicMarketsServed?: string | null;
  professionalCertificationsLicensesDesignations?: string | null;
  phoenixCapitalInterest?: string | null;
  businessBrokerageLicenseTypes?: string[] | null;
  licensingDetails?: BrokerLicenseDetailDto[] | null;
  website?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  assignedC1LiaisonUserId?: string | null;
  assignedC0ManagerUserId?: string | null;
  notes?: string | null;
  // v3.0 enhancements (all optional — omitted fields preserve server value)
  tags?: string[] | null;
  hasDisciplinaryAction?: boolean | null;
  disciplinaryNotes?: string | null;
  annualRefresherDueDate?: string | null;
  /** Staff-captured email; persisted to Broker.PortalLoginEmail and used when portal access is granted. */
  portalLoginEmail?: string | null;
  // Broker Channel Batch (WS7a) — qualifications-profile fields. All
  // optional: null/omitted = leave unchanged; "" clears a string field
  // (BrokerService.Apply's WS7a block).
  industrySpecialties?: string[] | null;
  transactionSizeRange?: string | null;
  largestTransactionClosed?: number | null;
  totalCareerTransactionVolume?: number | null;
  averageDealsPerYear?: number | null;
  currentActiveListings?: number | null;
  isIbbaMember?: boolean | null;
  isMaSourceMember?: boolean | null;
  otherProfessionalMemberships?: string | null;
  professionalBiography?: string | null;
  dealSourcingNotes?: string | null;
  languagesSpoken?: string | null;
}

export interface BrokerAlertDto {
  brokerId: string;
  companyName: string;
  alertType: BrokerAlertType;
  expiresOn: string;
  daysUntilExpiry: number;
  severity: BrokerAlertSeverity;
}

export interface BrokerPipelineDto {
  stages: Record<BrokerStatus, BrokerSummaryDto[]>;
  totalBrokers: number;
}

// --- Broker Channel Batch (WS7b) — Certifications checklist --------------

export type BrokerCertificationStatus = "Pending" | "Recorded" | "Verified" | "Waived";

export interface BrokerCertificationDto {
  id: string;
  brokerId: string;
  code: string;
  title: string;
  shortDescription?: string | null;
  description: string;
  requiredEvidence?: string | null;
  sortOrder: number;
  status: BrokerCertificationStatus;
  evidenceJson?: string | null;
  notes?: string | null;
  recordedAt?: string | null;
  recordedByUserId?: string | null;
  verifiedAt?: string | null;
  verifiedByUserId?: string | null;
  reportDocumentId?: string | null;
  /** Seeded broker-scoped certification report template id; null for an
   *  admin-added catalog item with no seeded template. */
  reportTemplateId?: string | null;
  /** When the renewal-request automation last emailed the broker for this
   *  item (BRK-INS / BRK-LIC only); null / absent when never sent. */
  lastAutoRenewalRequestSentAtUtc?: string | null;
  /** Waiver attestation — non-null only for a Waived row. */
  waivedReason?: string | null;
  waivedByUserId?: string | null;
  waivedAt?: string | null;
  /** Server-computed for the current caller (liaison/admin waiver gate +
   *  status eligibility); read these, never re-derive the gate. Optional so
   *  inline test fixtures keep compiling (LoanSummaryDto precedent). */
  canWaive?: boolean;
  canUnwaive?: boolean;
}

// --- Broker Channel Batch (WS5) — stage readiness -------------------------

export interface BrokerStageReadinessCertificationItemDto {
  code: string;
  title: string;
  status: BrokerCertificationStatus;
  reportFiled: boolean;
}

export interface BrokerStageGateDto {
  stageFrom?: string | null;
  stageTo: string;
  label: string;
  certified: boolean;
}

/** Auto-evaluable facts backing the WS5 soft stage-checklists. */
export interface BrokerStageReadinessDto {
  latestQualificationRecommendation?: QualificationResult | null;
  applicationApproved: boolean;
  hasContactDetails: boolean;
  hasState: boolean;
  portalAccessGranted: boolean;
  liaisonAssigned: boolean;
  eoOnFile: boolean;
  eoCurrent: boolean;
  licenseOnFile: boolean;
  trainingCompleted: boolean;
  certificationItems: BrokerStageReadinessCertificationItemDto[];
  stageGates: BrokerStageGateDto[];
  /** Onboarding differentiation — a Junior broker is covered by their
   *  brokerage's E&O, so the E&O advisory rows are suppressed. Optional so
   *  inline test fixtures keep compiling (LoanSummaryDto precedent). */
  isJunior?: boolean;
  /** Escalation PR-2 (F4) — this broker came from a Buyer CRM listing agent, so
   *  the recruitment ladder does not apply to them at all. The server already
   *  empties certificationItems; this suppresses the rows computed straight off
   *  Broker columns, which the catalog filter cannot reach. Optional, same
   *  reason as isJunior. */
  isListingAgentSourced?: boolean;
}

export interface BrokerListQuery extends PagedQuery {
  status?: BrokerStatus;
  source?: BrokerSource;
  state?: string;
  assignedLiaisonUserId?: string;
  onlyAlerts?: boolean;
  assignedToMe?: boolean;
  /** Broker Status Refactor: Pipeline (default) / Archive / All. */
  view?: BrokerListView;
}

// --- Broker money core (Independent↔Junior conversion + per-deal absorb) ---
// One conversion endpoint drives both entry points (broker record + brokerage
// roster). The type/firm change is applied in place; when a Junior conversion
// would move the payee on a broker's outstanding deals, each deal carries an
// explicit absorb / keep-paid-direct decision plus a written-authorization
// attestation. See RecipientResolvers.LoanBrokerResolver (Lever C).

/** A deal a broker is still attached to at conversion time — the per-deal
 *  absorb picklist source. */
export interface BrokerOutstandingDealDto {
  dealId: string;
  dealNumber: string;
  stage: string;
  loanId?: string | null;
  isFunded: boolean;
  /** Who is paid TODAY on this deal ("Broker" = direct, "Brokerage" = absorbed). */
  currentPayee: "Broker" | "Brokerage";
  /** Already permanently absorbed into a firm — the row is locked. */
  isAbsorbed: boolean;
  /** The deal stays paid direct to the broker (the safe default decision). */
  stayDirect: boolean;
}

export interface BrokerConversionAttestation {
  confirmed: boolean;
  note?: string | null;
}

export interface BrokerConversionDealDecision {
  dealId: string;
  /** true = absorb into the covering firm; false = keep paid direct. */
  absorb: boolean;
}

export interface BrokerConversionRequest {
  requestedType: BrokerType;
  coveringBrokerageId?: string | null;
  dealDecisions?: BrokerConversionDealDecision[];
  attestation?: BrokerConversionAttestation | null;
}

export interface BrokerConversionResultDto {
  brokerId: string;
  brokerType: BrokerType;
  coveringBrokerageId?: string | null;
  absorbedDealCount: number;
  stayDirectDealCount: number;
}

export interface BrokerDealAbsorbResultDto {
  dealId: string;
  brokerageId: string;
  alreadyAbsorbed: boolean;
}

export const brokersApi = {
  list: (query?: BrokerListQuery) => api.get<PagedResult<BrokerSummaryDto>>("/brokers", query),
  pipeline: (assignedToMe?: boolean) =>
    api.get<BrokerPipelineDto>("/brokers/pipeline", { assignedToMe }),
  alerts: () => api.get<BrokerAlertDto[]>("/brokers/alerts"),
  get: (id: string) => api.get<BrokerDetailDto>(`/brokers/${id}`),
  // Broker "Import from files" (WS-E/WS-D) — kicks the AI scan of the
  // broker's filed documents; the resulting job state polls back on get().
  importFromFiles: (id: string) =>
    api.post<BrokerQualificationImportTriggerResult>(`/brokers/${id}/import-from-files`),
  create: (body: UpsertBrokerPayload) => api.post<BrokerDetailDto>("/brokers", body),
  update: (id: string, body: UpsertBrokerPayload) => api.put<BrokerDetailDto>(`/brokers/${id}`, body),
  delete: (id: string) => api.delete<void>(`/brokers/${id}`),
  transitions: (id: string) => api.get<BrokerStatus[]>(`/brokers/${id}/valid-transitions`),
  changeStatus: (id: string, targetStatus: BrokerStatus, reason?: string) =>
    api.post<BrokerDetailDto>(`/brokers/${id}/status`, { targetStatus, reason }),
  submitQualification: (id: string, body: {
    greenFlagsJson?: string | null;
    redFlagsJson?: string | null;
    dealFlowEstimate?: number | null;
    transactionSizeRange?: string | null;
    isIbbaMember: boolean;
    isMaSourceMember: boolean;
    hasWebsite: boolean;
    hasCrm: boolean;
    primarilyResidential: boolean;
    overallAssessment?: string | null;
    recommendation: QualificationResult;
    /** Broker CRM Polish §3 — preferred over the booleans above. */
    qualificationCheckIds?: string[];
  }) => api.post<BrokerQualificationDto>(`/brokers/${id}/qualifications`, body),
  // Broker page fixes — edit / remove an existing qualification. The body
  // shape matches submitQualification; authorization (author or admin) is
  // enforced server-side.
  updateQualification: (id: string, qualificationId: string, body: {
    greenFlagsJson?: string | null;
    redFlagsJson?: string | null;
    dealFlowEstimate?: number | null;
    transactionSizeRange?: string | null;
    isIbbaMember: boolean;
    isMaSourceMember: boolean;
    hasWebsite: boolean;
    hasCrm: boolean;
    primarilyResidential: boolean;
    overallAssessment?: string | null;
    recommendation: QualificationResult;
    qualificationCheckIds?: string[];
  }) => api.put<BrokerQualificationDto>(`/brokers/${id}/qualifications/${qualificationId}`, body),
  deleteQualification: (id: string, qualificationId: string) =>
    api.delete<void>(`/brokers/${id}/qualifications/${qualificationId}`),
  // upsertTraining was retired by Certifications Module v1.0 PR 3.
  // Training attestations are now CertificationRecord rows; see
  // certificationsApi.certify in @/api/certifications.
  upsertContact: (id: string, contactId: string | undefined, body: {
    firstName: string;
    lastName: string;
    title?: string | null;
    email?: string | null;
    phone?: string | null;
    isPrimary: boolean;
    isActive: boolean;
  }) => api.post<BrokerContactDto>(
    `/brokers/${id}/contacts${contactId ? `?contactId=${contactId}` : ""}`, body),
  deleteContact: (id: string, contactId: string) =>
    api.delete<void>(`/brokers/${id}/contacts/${contactId}`),
  // The legacy broker-scoped file endpoints (documents / uploadDocument /
  // downloadDocument / generateMba) were retired by Certifications Module
  // v1.0 PR 3. Broker files now use the platform-wide Documents tab
  // (@/pages/documents/components/DocumentPanel per Document Access
  // Standard v1.1 §8); MBA execution is recorded as a certification on
  // the Certifications tab.
  bulkAssign: (brokerIds: string[], field: BrokerAssignmentField, userId: string | null) =>
    api.post<BulkBrokerActionResultDto>("/brokers/bulk/assign", { brokerIds, field, userId }),
  // Ownership/Assignment §11 — single-broker liaison reassignment.
  assign: (id: string, userId: string) =>
    api.put<BrokerDetailDto>(`/brokers/${id}/assign`, { userId }),
  bulkStatus: (brokerIds: string[], targetStatus: BrokerStatus, reason: string | null) =>
    api.post<BulkBrokerActionResultDto>("/brokers/bulk/status", {
      brokerIds,
      targetStatus,
      reason,
    }),
  // Layout Compliance v1.0 PR 6 — per-broker `calls` / `logCall` /
  // `brokerTasks` / `createBrokerTask` retired. Communications and Tasks
  // for a Broker now live on the canonical cross-cutting tabs
  // (CommunicationsTabPanel / TaskListMini), which read the central
  // CommunicationLogEntries and WorkTasks tables. Legacy CallLog and
  // BrokerTask rows for this broker were backfilled into those tables by
  // migration 20270401000000_LayoutBroker_BackfillCommLogAndWorkTasks.

  // --- Broker Status Refactor — archive / disqualify / reactivate ---
  archive: (id: string, targetStatus: ArchiveTargetStatus, reason: string) =>
    api.post<BrokerDetailDto>(`/brokers/${id}/archive`, { targetStatus, reason }),
  disqualify: (id: string, reason: string) =>
    api.post<BrokerDetailDto>(`/brokers/${id}/disqualify`, { reason }),
  reactivate: (id: string, targetStatus: BrokerStatus, notes?: string) =>
    api.post<BrokerDetailDto>(`/brokers/${id}/reactivate`, { targetStatus, notes }),

  // UAT FixBatch-04 FIND-13a — broker-side invite-to-apply, mirroring the
  // lead-side flow. The three-state status drives the header affordance.
  inviteStatus: (id: string) =>
    api.get<BrokerInviteStatusDto>(`/brokers/${id}/invite-status`),
  inviteToApply: (
    id: string,
    body: {
      collectionBundleIds: string[];
      sendPortalAccessOnly: boolean;
      // PR 2 — onboarding type chooser: the broker's chosen type + covering firm.
      target?: LeadConversionTarget;
      brokerageId?: string | null;
    },
  ) => api.post<LeadInviteToApplyResultDto>(`/brokers/${id}/invite-to-apply`, body),

  // --- Broker money core (Independent↔Junior conversion) -----------------
  // The broker's outstanding deals feed the per-deal absorb picklist; the
  // conversion applies the type/firm change + the absorb decisions atomically.
  outstandingDeals: (id: string) =>
    api.get<BrokerOutstandingDealDto[]>(`/brokers/${id}/outstanding-deals`),
  convert: (id: string, body: BrokerConversionRequest) =>
    api.post<BrokerConversionResultDto>(`/brokers/${id}/convert`, body),
  absorbDeal: (
    brokerId: string,
    dealId: string,
    attestation: BrokerConversionAttestation,
  ) =>
    api.post<BrokerDealAbsorbResultDto>(
      `/brokers/${brokerId}/deals/${dealId}/absorb`,
      { attestation },
    ),

  // --- Broker Channel Batch (WS5) — soft stage-readiness checklist -------
  stageReadiness: (id: string) =>
    api.get<BrokerStageReadinessDto>(`/brokers/${id}/stage-readiness`),

  // --- Broker Channel Batch (WS7b) — Certifications checklist ------------
  // Reads gate on Brokers.Read; mutations on Brokers.Qualify.
  listCertifications: (brokerId: string) =>
    api.get<BrokerCertificationDto[]>(`/brokers/${brokerId}/certifications`),
  recordCertification: (
    brokerId: string,
    code: string,
    body: { evidenceJson?: string | null; notes?: string | null },
  ) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/record`, body),
  verifyCertification: (brokerId: string, code: string, notes?: string | null) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/verify`, { notes: notes ?? null }),
  reopenCertification: (brokerId: string, code: string) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/reopen`),
  waiveCertification: (brokerId: string, code: string, reason: string) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/waive`, { reason }),
  unwaiveCertification: (brokerId: string, code: string) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/unwaive`),
  fileCertificationReport: (brokerId: string, code: string, reportSubmissionId: string) =>
    api.post<BrokerCertificationDto>(
      `/brokers/${brokerId}/certifications/${code}/file-report`, { reportSubmissionId }),
};
